import React from 'react'
import { ResponsivePie } from '@nivo/pie'
import { ResponsiveBar } from '@nivo/bar'
import { ResponsiveLine } from '@nivo/line'
import { ResponsiveScatterPlot } from '@nivo/scatterplot'
import { ResponsiveAreaBump } from '@nivo/bump'
import { ColorSchemeId } from '@nivo/colors'
import { LinearProgress } from '@mui/material'

export interface ChartConfig {
  chartType: 'pie' | 'bar' | 'line' | 'scatter' | 'area' | 'histogram'
  title: string
  xAxis: { label: string; dataKey: string }
  yAxis: { label: string; dataKey: string }
  data: Array<{ xValue: string | number; yValue: number }>
  options?: {
    colorScheme?: ColorSchemeId
    interactive?: boolean
    legend?: boolean
    innerRadius?: number
    stacked?: boolean
    pointSize?: number
  }
}

interface NivoContainerProps {
  title: string
  children: React.ReactNode
}

const NivoContainer: React.FC<NivoContainerProps> = ({ title, children }) => {
  const defaultHeight = 400
  return (
    <div
      className="flex flex-col items-center justify-center w-full"
      style={{ height: defaultHeight }}
    >
      {title && <h3 className="text-center text-base font-bold mb-4">{title}</h3>}
      {children}
    </div>
  )
}

const NivoChart: React.FC<{ config: ChartConfig }> = ({ config }) => {
  const { chartType, data, xAxis, yAxis, options, title } = config

  if (!data || data.length === 0) {
    return <LinearProgress />
  }

  // Filter and validate data to prevent NaN values in `yValue`
  const validData = data
    .filter((d) => d.yValue != null && !isNaN(d.yValue))
    .map((d) => ({
      ...d,
      yValue: d.yValue ?? 0,
    }))
  if (validData.length === 0) {
    return <></>
  }
  const maxItemsInRow = 5
  const hasLongColumnName = data.some((d) => d.xValue.toString().length > 20)
  const columnLayout = data.length > maxItemsInRow || hasLongColumnName
  // Dynamic legend configuration based on number of items
  const legendConfig = [
    {
      anchor: 'right' as const,
      direction: columnLayout ? ('column' as const) : ('row' as const),
      translateY: columnLayout ? 0 : 56,
      itemWidth: columnLayout ? 80 : 100, // Adjust width for row layout
      itemHeight: 24,
      symbolSize: 18,
      symbolShape: 'circle' as const,
    },
  ]

  switch (chartType) {
    case 'pie':
      return (
        <NivoContainer title={title}>
          <ResponsivePie
            data={validData.map((d) => ({
              id: d.xValue.toString(),
              label: d.xValue.toString(),
              value: d.yValue,
              tooltipLabel: `${d.xValue}: ${d.yValue.toLocaleString()}`,
            }))}
            margin={{ top: 40, right: 120, bottom: 80, left: 80 }}
            innerRadius={options?.innerRadius || 0.5}
            padAngle={0.7}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            colors={{ scheme: options?.colorScheme || 'nivo' }}
            borderWidth={1}
            borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor="#333333"
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: 'color' }}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
            enableArcLabels={options?.interactive}
            tooltip={({ datum }) => (
              <div
                style={{
                  padding: '8px 12px',
                  background: '#ffffff',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  color: '#333333',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                <strong style={{ color: datum.color }}>{datum.data.tooltipLabel}</strong>
              </div>
            )}
            theme={{
              tooltip: {
                container: {
                  fontSize: 12,
                },
              },
              legends: {
                text: {
                  fontSize: 12,
                  fill: '#333333',
                },
              },
            }}
            legends={options?.legend ? legendConfig : []}
            animate={true}
            motionConfig="gentle"
          />
        </NivoContainer>
      )
    case 'bar':
    case 'histogram':
      return (
        <NivoContainer title={title}>
          <ResponsiveBar
            data={validData.map((d) => ({
              [xAxis.dataKey]: d.xValue,
              [yAxis.dataKey]: d.yValue,
              tooltipLabel: `${xAxis.label} ${d.xValue}: ${d.yValue.toLocaleString()} ${
                yAxis.label
              }`,
            }))}
            keys={[yAxis.dataKey]}
            indexBy={xAxis.dataKey}
            margin={{ top: 50, right: 130, bottom: 80, left: 80 }}
            padding={0.3}
            valueScale={{ type: 'linear' }}
            indexScale={{ type: 'band', round: true }}
            colors={{ scheme: options?.colorScheme || 'nivo' }}
            borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: xAxis.label,
              legendPosition: 'middle',
              legendOffset: 55,
              truncateTickAt: 0,
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: yAxis.label,
              legendPosition: 'middle',
              legendOffset: -60,
              format: (value: number | string) =>
                typeof value === 'number'
                  ? value >= 1000
                    ? `${(value / 1000).toFixed(1)}k`
                    : value.toLocaleString()
                  : value,
            }}
            enableLabel={false}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={{
              from: 'color',
              modifiers: [['darker', 1.6]],
            }}
            role="application"
            ariaLabel={title}
            barAriaLabel={(e) => `${xAxis.label} ${e.indexValue}: ${e.value} ${yAxis.label}`}
            tooltip={({ data, color }) => (
              <div
                style={{
                  padding: '8px 12px',
                  background: '#ffffff',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  color: '#333333',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                <strong style={{ color }}>{data.tooltipLabel}</strong>
              </div>
            )}
            theme={{
              axis: {
                domain: {
                  line: {
                    stroke: '#dddddd',
                    strokeWidth: 1,
                  },
                },
                ticks: {
                  line: {
                    stroke: '#dddddd',
                    strokeWidth: 1,
                  },
                  text: {
                    fontSize: 12,
                    fill: '#666666',
                  },
                },
                legend: {
                  text: {
                    fontSize: 13,
                    fontWeight: 600,
                    fill: '#333333',
                  },
                },
              },
              grid: {
                line: {
                  stroke: '#dddddd',
                  strokeWidth: 1,
                  strokeDasharray: '4 4',
                },
              },
              legends: {
                text: {
                  fontSize: 12,
                  fill: '#333333',
                },
              },
            }}
            animate={true}
            motionConfig="gentle"
            legends={
              options?.legend ? legendConfig.map((legend) => ({ ...legend, dataFrom: 'keys' })) : []
            }
            groupMode={
              chartType === 'histogram' ? 'stacked' : options?.stacked ? 'stacked' : 'grouped'
            }
          />
        </NivoContainer>
      )
    case 'line':
      return (
        <NivoContainer title={title}>
          <ResponsiveLine
            data={[
              {
                id: 'series',
                data: validData.map((d) => ({
                  x: d.xValue,
                  y: d.yValue,
                  tooltipLabel: `${xAxis.label} ${d.xValue}: ${d.yValue.toLocaleString()} ${
                    yAxis.label
                  }`,
                })),
              },
            ]}
            margin={{ top: 50, right: 110, bottom: 80, left: 80 }}
            xScale={{ type: 'point' }}
            yScale={{
              type: 'linear',
              min: 'auto',
              max: 'auto',
              stacked: false,
              reverse: false,
            }}
            curve="monotoneX"
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: xAxis.label,
              legendOffset: 55,
              legendPosition: 'middle',
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: yAxis.label,
              legendOffset: -60,
              legendPosition: 'middle',
              format: (value) =>
                typeof value === 'number'
                  ? value >= 1000
                    ? `${(value / 1000).toFixed(1)}k`
                    : value.toLocaleString()
                  : value,
            }}
            colors={{ scheme: options?.colorScheme || 'nivo' }}
            lineWidth={3}
            pointSize={options?.pointSize || 8}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={-12}
            enableArea={true}
            areaOpacity={0.15}
            enablePoints={options?.interactive !== false}
            enableGridX={true}
            enableGridY={true}
            useMesh={true}
            animate={true}
            motionConfig="gentle"
            tooltip={({ point }) => (
              <div
                style={{
                  padding: '8px 12px',
                  background: '#ffffff',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  color: '#333333',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                <strong style={{ color: point.serieColor }}>
                  {`${config.xAxis.label}: ${point.data.x}, ${config.yAxis.label}: ${point.data.y}`}
                </strong>
              </div>
            )}
            theme={{
              axis: {
                domain: {
                  line: {
                    stroke: '#dddddd',
                    strokeWidth: 1,
                  },
                },
                ticks: {
                  line: {
                    stroke: '#dddddd',
                    strokeWidth: 1,
                  },
                  text: {
                    fontSize: 12,
                    fill: '#666666',
                  },
                },
                legend: {
                  text: {
                    fontSize: 13,
                    fontWeight: 600,
                    fill: '#333333',
                  },
                },
              },
              grid: {
                line: {
                  stroke: '#dddddd',
                  strokeWidth: 1,
                  strokeDasharray: '4 4',
                },
              },
              legends: {
                text: {
                  fontSize: 12,
                  fill: '#333333',
                },
              },
            }}
            legends={options?.legend ? legendConfig : []}
          />
        </NivoContainer>
      )
    case 'scatter':
      return (
        <NivoContainer title={title}>
          <ResponsiveScatterPlot
            data={[
              {
                id: 'scatterSeries',
                data: validData.map((d) => ({
                  x: d.xValue,
                  y: d.yValue,
                  tooltipLabel: `${xAxis.label} ${d.xValue}: ${d.yValue.toLocaleString()} ${
                    yAxis.label
                  }`,
                })),
              },
            ]}
            margin={{ top: 50, right: 110, bottom: 80, left: 80 }}
            xScale={{ type: 'linear', min: 'auto', max: 'auto' }}
            yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: xAxis.label,
              legendOffset: 55,
              legendPosition: 'middle',
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: yAxis.label,
              legendOffset: -60,
              legendPosition: 'middle',
              format: (value) =>
                typeof value === 'number'
                  ? value >= 1000
                    ? `${(value / 1000).toFixed(1)}k`
                    : value.toLocaleString()
                  : value,
            }}
            colors={{ scheme: options?.colorScheme || 'nivo' }}
            enableGridX={true}
            enableGridY={true}
            animate={true}
            motionConfig="gentle"
            tooltip={({ node }) => (
              <div
                style={{
                  padding: '8px 12px',
                  background: '#ffffff',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  color: '#333333',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                <strong style={{ color: node.color }}>
                  {`${config.xAxis.label}: ${node.data.x}, ${config.yAxis.label}: ${node.data.y}`}
                </strong>
              </div>
            )}
            theme={{
              axis: {
                domain: {
                  line: {
                    stroke: '#dddddd',
                    strokeWidth: 1,
                  },
                },
                ticks: {
                  line: {
                    stroke: '#dddddd',
                    strokeWidth: 1,
                  },
                  text: {
                    fontSize: 12,
                    fill: '#666666',
                  },
                },
                legend: {
                  text: {
                    fontSize: 13,
                    fontWeight: 600,
                    fill: '#333333',
                  },
                },
              },
              grid: {
                line: {
                  stroke: '#dddddd',
                  strokeWidth: 1,
                  strokeDasharray: '4 4',
                },
              },
              legends: {
                text: {
                  fontSize: 12,
                  fill: '#333333',
                },
              },
            }}
            legends={options?.legend ? legendConfig : []}
          />
        </NivoContainer>
      )
    case 'area':
      return (
        <NivoContainer title={title}>
          <ResponsiveAreaBump
            data={[
              { id: 'areaSeries', data: validData.map((d) => ({ x: d.xValue, y: d.yValue })) },
            ]}
            margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
            colors={{ scheme: options?.colorScheme || 'nivo' }}
          />
        </NivoContainer>
      )
    default:
      return <div>Chart type not supported</div>
  }
}

export default NivoChart
